import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import { ListCacheManager } from '@/data/hook/query/v2/CacheFacade';
import useListService from '@/data/hook/useListService';
import { examActivityQueries } from '@/data/query-keys/examActivityQueryKey';
import {
  createExamActivity,
  deleteExamActivity,
  ExamActivityDTO,
  updateExamActivity,
  updateExamQuestionsOrder,
} from '@/data/services/examActivityServices';
import { ActivityQuestionOrder } from '@/data/services/activityServices';
import Activity from '@/models/Activity';
import ExamArea from '@/models/ExamArea';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import { reorder } from '@/utils/list';
import compact from 'lodash/compact';

type UseExamActivitiesProps = {
  examAreas: ExamArea[];
  examAreaCacheManager: ListCacheManager<ExamArea>;
};

type CreateMutationArgs = {
  examAreaId: number;
  data: ExamActivityDTO;
};

type UpdateMutationArgs = {
  examAreaId: number;
  activityId: number;
  data: Partial<ExamActivityDTO>;
};

type DeleteMutationArgs = {
  examAreaId: number;
  activityId: number;
};

type ReorderMutationArgs = {
  examAreaId: number;
  order: ActivityQuestionOrder[];
};

export type VersionedActivityArgs = {
  examAreaId: number;
  previousId: number;
  versionedId: number;
};

const useExamActivities = ({
  examAreas,
  examAreaCacheManager,
}: UseExamActivitiesProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.examWorkspace.questions',
  });

  const examAreaIds = useMemo(() => examAreas.map(ea => ea.id), [examAreas]);

  const examAreaById = useMemo(
    () => new Map(examAreas.map(ea => [ea.id, ea])),
    [examAreas],
  );

  const {
    results: activities,
    isInitialLoading,
    cacheManager,
  } = useListService({
    ...examActivityQueries.list({ examAreaIds }),
    enabled: examAreaIds.length > 0,
    staleTime: REQUEST_STALE_TIME_IN_MS,
    keepPreviousData: true,
  });

  const previousIdByVersionedId = useRef(new Map<number, number>());

  const activitiesByAreaId = useMemo(() => {
    const map = new Map<number, Activity[]>();
    examAreas.forEach(ea => {
      const ids = new Set(
        (ea.activities ?? []).flatMap(({ id }) => [
          id,
          previousIdByVersionedId.current.get(id) ?? id,
        ]),
      );
      map.set(
        ea.id,
        activities.filter(activity => ids.has(activity.id)),
      );
    });
    return map;
  }, [examAreas, activities]);

  const replaceVersionedActivity = useCallback(
    ({ examAreaId, previousId, versionedId }: VersionedActivityArgs) => {
      previousIdByVersionedId.current.set(versionedId, previousId);
      const area = examAreaById.get(examAreaId);
      if (!area) return;
      examAreaCacheManager.update({
        ...area,
        activities: (area.activities ?? []).map(activity =>
          activity.id === previousId
            ? { ...activity, id: versionedId }
            : activity,
        ),
      });
    },
    [examAreaById, examAreaCacheManager],
  );

  const createMutation = useMutation({
    mutationFn: ({ examAreaId, data }: CreateMutationArgs) =>
      createExamActivity(examAreaId, data),
    onSuccess: (newActivity, { examAreaId }) => {
      cacheManager.add(newActivity);
      const area = examAreaById.get(examAreaId);
      if (area) {
        examAreaCacheManager.update({
          ...area,
          activities: [
            ...(area.activities ?? []),
            { id: newActivity.id, numberOfQuestions: 0 },
          ],
        });
      }
      alert.success(t('createSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ examAreaId, activityId, data }: UpdateMutationArgs) =>
      updateExamActivity(examAreaId, activityId, data),
    onSuccess: updated => {
      cacheManager.update(updated);
      alert.success(t('updateSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  const reorderMutation = useMutation({
    onMutate: ({ order }: ReorderMutationArgs) => {
      const allQuestions = activities.flatMap(a => a.questions ?? []);
      const reorderedActivities = compact(order
        .map((block, index) => {
          const activity = activities.find(a => a.id === block.id);
          if (!activity) return null;
          const questions = reorder(
            allQuestions,
            block.questions.map(q => q.id),
          );
          return { ...activity, questions, order: index + 1 };
        }))

      const affectedIds = new Set(reorderedActivities.map(a => a.id));
      const queue = [...reorderedActivities];
      const merged = activities.map(activity =>
        affectedIds.has(activity.id) ? queue.shift() : activity,
      );
      cacheManager.set(compact(merged));
    },
    mutationFn: ({ examAreaId, order }: ReorderMutationArgs) =>
      updateExamQuestionsOrder(examAreaId, order),
    onSuccess: () => {
      alert.success(t('reorderQuestionsSuccess'));
    },
    onError: err => {
      cacheManager.undo();
      alert.error(getErrorMessage(err));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: ({ examAreaId, activityId }: DeleteMutationArgs) =>
      deleteExamActivity(examAreaId, activityId),
    onSuccess: (_, { examAreaId, activityId }) => {
      cacheManager.remove(activityId);
      const area = examAreaById.get(examAreaId);
      if (area) {
        examAreaCacheManager.update({
          ...area,
          activities: (area.activities ?? []).filter(
            activity => activity.id !== activityId,
          ),
        });
      }
      alert.success(t('deleteSuccess'));
    },
    onError: err => alert.error(getErrorMessage(err)),
  });

  return {
    activities,
    activitiesByAreaId,
    cacheManager,
    isLoading: isInitialLoading,
    createActivity: createMutation.mutateAsync,
    isCreating: createMutation.isPending,
    reorderActivity: reorderMutation.mutate,
    isReordering: reorderMutation.isPending,
    updateActivity: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    deleteActivity: deleteMutation.mutate,
    replaceVersionedActivity,
  };
};

export default useExamActivities;
